import {html, HTMLTemplateResult, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {get, translate} from "lit-translate";
import {container} from "tsyringe";
import {AbstractDialogComponent} from "../../ha-dialog/abstract-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {DialogHelper} from "../../../helpers/dialog.helper";
import {NavigationHelper} from "../../../helpers/navigation.helper";
import {BackendClient} from "../../../connectivity/backend-client";
import {CommandBus} from "../../../services/command-bus.service";
import {KnowledgeObject} from "../../../domain/entities/knowledge-object.entity";
import type {KnowledgeObjectType} from "../../../domain/entities/knowledge-object-type.type";
import {KnowledgeObjectRepository} from "../../../domain/repositories/knowledge-object.repository";
import {GlobalNotificationService} from "../../../services/global-notification.service";
import type {RadioButtonItem} from "../../_inputs/ha-input-checkbox/radio-button-item.interface";
import {
    getDialogTitleKey,
    getInitialTitle,
    getSubmitButtonKey,
    isRenameMode,
} from "./knowledge-object-edit-mode.helper";
import {submitKnowledgeObjectEdit} from "./knowledge-object-edit-submit";

/**
 * Two-mode dialog for creating or renaming a knowledge object (whiteboard or text document).
 *
 * - Create mode (knowledgeObject === null): "Neues Wissens-Objekt" with a type picker
 *   (Whiteboard / Textdokument, see `_typeItems`) → "Erstellen" → delegates to
 *   `KnowledgeObjectRepository.create()` and navigates to the new object's detail route.
 * - Rename mode (knowledgeObject set): "Wissens-Objekt umbenennen" → "Umbenennen"
 *   → dispatches `UpdateKnowledgeObjectMetadataCommand` to update the title.
 *
 * Submit-routing and slug computation live in the pure helper modules
 * (`knowledge-object-edit-mode.helper.ts`, `knowledge-object-edit-submit.ts`)
 * so the dialog itself is a thin UI shell.
 */
@customElement(`ha-dialog-knowledge-object-edit`)
export class HaDialogKnowledgeObjectEditComponent extends AbstractDialogComponent {

    @property({type: Object})
    public knowledgeObject: KnowledgeObject | null = null;

    @state()
    private _title = ``;

    /** Selected object type — create-mode only; reset to whiteboard per open. */
    @state()
    private _selectedType: KnowledgeObjectType = `whiteboard`;

    /** Picked image file — create-mode + `image` type only; reset per open. Unused while the
     *  picker does not offer `image` (see `_typeItems`, feature plan 0056). */
    @state()
    private _selectedFile: File | null = null;

    @state()
    private _isSubmitting = false;

    public constructor() {
        super();
        this.closable = true;
    }

    private get _isRenameMode(): boolean {
        return isRenameMode(this.knowledgeObject);
    }

    /** Never true while the picker does not offer `image` — kept on purpose (see `_typeItems`). */
    private get _isImageMode(): boolean {
        return !this._isRenameMode && this._selectedType === `image`;
    }

    private get _isSubmitEnabled(): boolean {
        if (this._title.trim().length === 0 || this._isSubmitting) {
            return false;
        }

        // An image object cannot be created without bytes — the submit helper
        // would reject immediately, so the button stays disabled instead.
        return !this._isImageMode || this._selectedFile !== null;
    }

    protected willUpdate(changedProperties: PropertyValues): void {
        if (changedProperties.has(`knowledgeObject`)) {
            this._title = getInitialTitle(this.knowledgeObject);
            this._selectedType = `whiteboard`;
            this._selectedFile = null;
            this.title = get(getDialogTitleKey(this.knowledgeObject));
        }
    }

    /**
     * Type choices for the create-mode picker (rename-mode hides it). Their icons (the same
     * ones as in the knowledge sidebar) come from dialogs.css via the radio's `data-item`.
     *
     * "Bild" is no longer offered (Anke, 2026-10-16): images enter through paste in the
     * whiteboard. The image path (`_selectedFile`, `_isImageMode`, the file picker in
     * `_renderMainContent`, the upload in knowledge-object-edit-submit.ts and the i18n keys
     * `type-image` / `label-file`) stays in place on purpose, unused, in case the option comes
     * back — re-adding the item here is all it takes (feature plan 0056).
     */
    private get _typeItems(): RadioButtonItem<KnowledgeObjectType>[] {
        return [
            {
                id: `type-whiteboard`,
                value: `whiteboard`,
                label: get(`wissen.dialog.knowledge-object-edit.type-whiteboard`),
            },
            {
                id: `type-document`,
                value: `document`,
                label: get(`wissen.dialog.knowledge-object-edit.type-document`),
            },
        ];
    }

    protected _renderMainContent(): HTMLTemplateResult {
        // Type picker only in create-mode — an existing object's type is fixed.
        const typePicker = this._isRenameMode
            ? html``
            : html`
                <ha-input-radio-group class="max9999-flexrow"
                                      data-type="type"
                                      label=${translate(`wissen.dialog.knowledge-object-edit.type-label`)}
                                      .items=${this._typeItems}
                                      .value=${this._selectedType}
                                      @value-changed=${this._onTypeChanged}
                ></ha-input-radio-group>
            `;

        // File picker only for a create-mode `image` object — unreachable while `_typeItems`
        // offers no `image` (kept on purpose, see there).
        const filePicker = this._isImageMode
            ? html`
                <ha-input-file class="max9999-flexrow"
                               data-type="file"
                               label=${translate(`wissen.dialog.knowledge-object-edit.label-file`)}
                               @value-changed=${this._onFileChanged}
                ></ha-input-file>
            `
            : html``;

        return html`
            <ha-form-knowledge-object>
                <header class="formular__header">
                    ${this._renderFormFooter()}
                </header>
                <main class="formular__main">
                    ${typePicker}
                    ${filePicker}
                    <ha-input-text class="max9999-flexrow"
                                   data-type="title"
                                   label=${translate(`wissen.dialog.knowledge-object-edit.label-title`)}
                                   .value=${this._title}
                                   @value-changed=${this._onTitleChanged}
                    ></ha-input-text>
                </main>
            </ha-form-knowledge-object>
        `;
    }

    protected _renderFormFooter(): HTMLTemplateResult {
        return html`
            <div class="button-group">
                <button type="button"
                        class="save buttonform-marker primary"
                        ?disabled=${!this._isSubmitEnabled}
                        @click=${this._onClickSubmit}

                        data-label=${translate(getSubmitButtonKey(this.knowledgeObject))}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="cancel buttonform-marker secondary"
                        @click=${this._onClickCancel}

                        data-label=${translate(`wissen.dialog.knowledge-object-edit.button-cancel`)}><div class="hover-effect"></div>
                </button>
            </div>
        `;
    }

    private _onTitleChanged(event: CustomEvent<{ value: string }>): void {
        this._title = event.detail.value ?? ``;
    }

    private _onTypeChanged(event: CustomEvent<{ value: KnowledgeObjectType }>): void {
        this._selectedType = event.detail.value ?? `whiteboard`;
        // A file picked for a type the user then abandoned must not be uploaded.
        this._selectedFile = null;
    }

    private _onFileChanged(event: CustomEvent<{ value: File | null }>): void {
        this._selectedFile = event.detail.value ?? null;
    }

    /**
     * Routes submit through the pure helper. Wraps the async call in a
     * closure-scoped guard + try/catch so async Lit handler errors never
     * bubble silently and the submit button can re-enable after failure.
     */
    private async _onClickSubmit(): Promise<void> {
        if (!this._isSubmitEnabled) {
            return;
        }

        const title = this._title.trim();
        const isRename = this._isRenameMode;
        const knowledgeObjectRepository = container.resolve(KnowledgeObjectRepository);
        this._isSubmitting = true;
        try {
            await submitKnowledgeObjectEdit(
                {
                    knowledgeObject: this.knowledgeObject,
                    title,
                    type: this._selectedType,
                    file: this._selectedFile,
                },
                {
                    knowledgeObjectRepository,
                    commandBus: container.resolve(CommandBus),
                    navigate: (url: string) => NavigationHelper.goto(url),
                    uploadFile: (file: File) => container.resolve(BackendClient).uploadFile(file),
                    // Compensating rollback: a KnowledgeObject whose content
                    // could not be written can never render, so it is deleted
                    // again instead of surviving as a broken row.
                    deleteKnowledgeObject: (knowledgeObject: KnowledgeObject) =>
                        knowledgeObjectRepository.delete(knowledgeObject),
                },
            );
            this._closeAfterSubmit(isRename);
        } catch (error) {
            console.error(`Failed to submit knowledge-object edit.`, error);
            // A console log is invisible to the user: without this the failed
            // upload/save is indistinguishable from a click that did nothing.
            container.resolve(GlobalNotificationService).error(
                get(`wissen.dialog.knowledge-object-edit.error-submit`),
                error instanceof Error ? error.message : undefined,
            );
        } finally {
            this._isSubmitting = false;
        }
    }

    private _onClickCancel(): void {
        this._closeAfterSubmit(false);
    }

    protected onClickClose(): void {
        DialogHelper.closeDialog(this.tagName);
        super.onClickClose();
    }

    private _closeAfterSubmit(_wasRename: boolean): void {
        DialogHelper.closeDialog(this.tagName);
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0,
        });
    }
}
